import type {FormErrors, UseFormReturnType} from '@mantine/form'
import {useEffect, useState, useCallback, useMemo} from 'react'
import {compactObjDeep, entries, isNullOrEmpty, notNullOrEmpty} from 'shared/collection'
import {Json, JsonObject, PlainObject, replaceUndefinedWithNull} from 'shared/type'
import {DeltaValuesDynamic, Paths, pathValue, withDeltaValues} from 'shared/paths'
import {useMemoValue} from 'shared/ui'
import {useOnDebounce} from './useOnDebounce'
import deepEqual from 'fast-deep-equal'
import {usePreventNav} from 'components/portal/usePreventNav'
import {useRouter} from 'next/router'
import {completeNavigationProgress, resetNavigationProgress, startNavigationProgress} from '@mantine/nprogress'
import {DateTime} from 'luxon'
import {isArray, isFunction, last, mapKeys, pick, pipe} from 'remeda'
import {useLatest} from 'shared/ui/useLatest'
import {showError} from 'components/notify'
import {usePortal} from 'components/portal/portal'
import {ZodSchema} from 'zod'
import {zodResolver} from '@mantine/form'
import {sb} from 'utils/db'
import {match, P} from 'ts-pattern'

const {debug: consoleDebug} = console
type OnChangeOptions = {noOnUpdate?: boolean; description?: string}

export type OnChangeProps<T> = {cfg: T; delta: DeltaValuesDynamic; description?: string}
export type OnUpdateProps<T> = OnChangeProps<T> & {updatedAt: DateTime}
/**
 * Use this to back your form fields when it's intended to be kept live with a db. Manages changes as 'delta' changes that are stored locally, and merged with updates from the db
 */
export const useDbForm = <T extends PlainObject | {[key: string]: unknown}>({
  stored: _stored,
  schema,
  // updatedAt,
  onUpdate,
  onChange,
  onReset,
  debounceSave,
  preventDirtyNav,
  debugName,
  autoSave,
  validator: validatorRaw
}: {
  stored?: T
  schema?: ZodSchema
  // updatedAt?: DateTime
  /** Called on each update to the form values  (debounced) */
  onUpdate?: (props: OnUpdateProps<T>) => void
  /** Called immediately on each change before debouncing */
  onChange?: (props: OnChangeProps<T>) => void
  onReset?: () => void
  /** when provided will automatically update the row, and if id is null will call onInsert with the initial cfg */
  autoSave?: {table: string; id?: string; insert?: (cfg: T) => void; postUpdate?: (cfg: T) => void}
  debounceSave?: number
  preventDirtyNav?: boolean
  debugName?: string
  validator?: (v: T) => FormErrors
}): UseDbForm<T> => {
  const {profile} = usePortal()
  // dirtyValue is a list of updates that needs to be performed. The key is a . delimited path
  const [dirty, setDirtyState] = useState<DeltaValuesDynamic>({})
  const [formErrors, setFormErrors] = useState<FormErrors>({})
  const isDirty = notNullOrEmpty(dirty)
  const stored = useMemoValue(_stored)
  const lastDotChange = (d: DeltaValuesDynamic) =>
    last(
      entries(d) // . means replace the whole object (no merging)
        .filter(([k]) => k == '.')
        .map(([_, v]) => v)
    ) as T
  const getValuesDynamic = useCallback(
    (d?: DeltaValuesDynamic): T =>
      // apply the delta values to the current stored. We compact it so that you can set undefined and it removes the property form the resulting object
      {
        if (isNullOrEmpty(d)) return stored as T
        return notNullOrEmpty(d) ? compactObjDeep(withDeltaValues(stored ?? ({} as any), d) as T) : (stored as T)
      },
    [stored]
  )

  const values = useMemo(() => {
    const res = getValuesDynamic(dirty)
    if (notNullOrEmpty(dirty) && debugName)
      consoleDebug('useDbForm deriving values from dirty', debugName, {dirty, stored, res})
    return res
  }, [dirty, getValuesDynamic])

  /**
   * replaces the current dirty with a new one
   */
  const setDirty = useCallback((v: DeltaValuesDynamic, options?: OnChangeOptions) => {
    const {stored, setDirtyState, debounce} = latest.current
    if (debugName) consoleDebug('useDbForm setDirty', debugName, options?.description, v)
    if (stored === undefined) return
    setDirtyState(v)
    if (!debounce) onUpdateLatest.current(v, options)
    return v
  }, [])

  /**
   * merges delta state into the current dirty state
   */
  const mergeDirty = useCallback((v: DeltaValuesDynamic, options?: OnChangeOptions) => {
    return setDirtyState((prev: DeltaValuesDynamic) => {
      const {debounce} = latest.current
      const newDirty = {...prev, ...v}
      if (debugName) consoleDebug('useDbForm mergeDirty', debugName, options?.description, {prev, newDirty})
      if (!debounce) onUpdateLatest.current(v, options)
      return newDirty
    })
  }, [])

  const reset = useCallback(() => {
    latest.current.setDirtyState({})
    setFormErrors({})
    onReset?.()
  }, [])

  const validator = validatorRaw ?? (schema ? zodResolver(schema) : undefined)
  const validate = useCallback(() => {
    if (!validator) return undefined
    const {values} = latest.current
    const errors = validator(values)
    setFormErrors(errors)
    return errors
  }, [schema])

  const clearDirty = useCallback(() => {
    latest.current.setDirtyState({})
  }, [])

  /** set values. How local changes to app funnel though to the local state and onChange events */
  const setValues = useCallback(
    (delta: DeltaValuesDynamic, description?: string) => {
      const {lastDotChange, dirty} = latest.current
      const lastDot = lastDotChange(delta)
      const newDirty = match(lastDot)
        .with(P.not(P.nullish), () => {
          setDirty(lastDot, {description})
          return lastDot
        })
        .otherwise(() => {
          mergeDirty(delta, {description})
          return {...dirty, ...delta} // merge dirty uses the latest, state but so we have a value to return, this is as is as we are calculating it now
        })
      const cfg = getValuesDynamic(newDirty)
      onChange?.({cfg, delta, description})
      return cfg
    },
    [dirty, setDirty]
  )

  const latest = useLatest({
    values: values,
    dirty,
    setDirtyState,
    isDirty,
    stored,
    lastDotChange,
    setValues,
    debounce: debounceSave,
    // updatedAt,
    formErrors,
    setFormErrors
  })

  const _onUpdate = async (delta: DeltaValuesDynamic, options?: OnChangeOptions) => {
    if (!delta) return
    if (notNullOrEmpty(delta)) {
      const updatedAt = DateTime.utc()
      const cfg = getValuesDynamic(delta)!
      if (!options?.noOnUpdate) {
        if (autoSave) {
          if (autoSave.insert && !autoSave.id) autoSave.insert(cfg)
          else if (autoSave.id) {
            if (!profile) {
              console.error('not auto-updating because profile is null')
            } else {
              const p = {
                table_name: autoSave.table,
                id: autoSave.id,
                cfg_updates: replaceUndefinedWithNull(delta) as Json,
                updated_at: updatedAt.toISO()!,
                profile_id: profile.id
              }

              if (debugName) consoleDebug('useDbForm autosaving', debugName, p)

              const r = await sb.rpc('update_cfg', p)
              if (r.error) {
                console.error('error updating cfg', r.error)
                showError(r.error, 'Failed to save question')
                return
              }
              if (debugName) consoleDebug('useDbForm autosaved', debugName, options?.description, {p, data: r.data})
              if (autoSave.postUpdate) {
                autoSave.postUpdate(r.data as T)
                const {dirty} = latest.current
                if (debugName)
                  consoleDebug('useDbForm post update', debugName, options?.description, {
                    dirty,
                    newData: r.data
                  })
                // we no longer verify that dirty is synced, we called the updates and it returned us the state, so there you go
                // const dirtyChangesNothing = deepEqual(r.data, withDeltaValues(stored as JsonObject, dirty))
                // immediately clear dirty with the results from the update if it is being handled. Use ref to get the latest dirty
                // the  main reason for this is there is a quick toggle and back, the stored doesn't actually change and we still have dirty that should be cleared
                // setDirty({}, {noOnUpdate: true}) // setDirty will clean it of superfluous state
              }
            }
          }
        }
        onUpdate?.({cfg, updatedAt, delta, description: options?.description})
      }
    }
  }

  const onUpdateLatest = useLatest(_onUpdate)

  useOnDebounce(dirty, {
    onChange: d => onUpdateLatest.current(d),
    enabled: dirty != null && !!debounceSave,
    debounce: debounceSave
  })

  // Execute pending save on unmount
  useEffect(() => {
    return () => {
      const {dirty, debounce} = latest.current
      if (dirty && notNullOrEmpty(dirty) && debounce) {
        // If there's pending dirty state and we're using debounce, execute it immediately
        onUpdateLatest.current(dirty)
      }
    }
  }, [])

  // when the stored value changes, clear out dirty thats the same
  useEffect(() => {
    const {dirty} = latest.current
    if (debugName) consoleDebug('useDbForm stored changed', debugName, {dirty, stored})
    if (!isDirty || !stored) return
    const storedWithDirty = compactObjDeep(withDeltaValues(stored as JsonObject, dirty))
    const storedClean = compactObjDeep(stored)
    // if applying our delta to the current stored is the same, then we don't need to do anything. This is a safer method of determining to clear dirty than evaluating each change
    const dirtyChangesNothing = deepEqual(storedClean, storedWithDirty)
    if (dirtyChangesNothing) {
      if (debugName) consoleDebug('useDbForm stored changed but dirty changes nothing', debugName, {dirty, stored})
      setDirty({}, {noOnUpdate: true, description: 'cleaning'}) // setDirty will clean it of superfluous state
    } else {
      if (debugName)
        console.log('useDbForm stored changed and dirty still changes something', debugName, {
          dirty,
          storedClean,
          storedWithDirty
        })
    }
  }, [stored])

  const setFieldValue = useCallback(
    (path: Paths<T>, value: Json | undefined, description?: string) => setValues({[path]: value}, description),
    [setValues]
  )

  /** like setFieldValue except there is no path type checking. Useful for dynamic paths */
  const setFieldValueDynamic = useCallback(
    (path: string[] | string, value: Json | undefined, description?: string) => {
      const {setValues} = latest.current
      return setValues({[Array.isArray(path) ? path.join('.') : path]: value}, description)
    },
    [setValues]
  )

  const getInputProps: GetInputProps<T> = (path, options) => {
    const {type, withError} = {type: 'input' as const, withError: true, ...options}
    const onChange = getInputOnChange<Json>(value => {
      if (isFunction(value)) {
        console.error('input change function not supported', value)
        return
      }
      setValues({[path]: value}, options?.description)
    })
    const values = getValuesDynamic(dirty)
    const value = pathValue(values, path as any)
    const onBlur =
      validator && withError ?
        () => {
          const error = validator(values)[path as any]
          setFormErrors({...formErrors, [path]: error})
        }
      : undefined
    const res = {
      onChange,
      error: withError ? formErrors[path as any] : undefined,
      onBlur,
      ...(type === 'checkbox' ? {checked: value, value} : {value})
    }
    return res
  }

  const router = useRouter()
  usePreventNav({
    preventNav: preventDirtyNav == true && isDirty,
    onPrevented: () => {
      resetNavigationProgress()
      startNavigationProgress()
    },
    onResume: resume => {
      completeNavigationProgress()
      router.push(resume.url, undefined, resume.options)
    },
    timeoutMs: 4000
  })

  useEffect(() => {
    if (debugName) consoleDebug('useDbForm dirty changed', debugName, dirty)
  }, [dirty])

  useEffect(() => {
    if (debugName) consoleDebug('useDbForm values changed', debugName, values)
  }, [values])

  return {
    values,
    setValues,
    setFieldValue,
    setFieldValueDynamic,
    getInputProps,
    isDirty,
    clearDirty,
    dirty,
    stored,
    validate,
    reset,
    onReset
  } as UseDbForm<T>
}

export type GetInputProps<Values> = UseFormReturnType<Values>['getInputProps']
export type LooseKeys<T> = Parameters<GetInputProps<T>>[0]

export type UseDbForm<T extends PlainObject | {[key: string]: unknown} = any> = {
  /** the  up to date local values */
  values?: T
  /** the upstream persisted values */
  stored?: T
  /** strong typing has been disabled as it would break typescript compiler (stack depth exceeded) */
  setValues: (values: DeltaValuesDynamic, description?: string) => T
  setFieldValue: (path: Paths<T>, value: Json | undefined, description?: string) => void
  setFieldValueDynamic: (path: string[] | string, value: Json | undefined, description?: string) => void
  getInputProps: GetInputProps<T>
  isDirty: boolean
  clearDirty: () => void
  dirty: DeltaValuesDynamic
  validate: () => FormErrors | undefined
  reset: () => void
  onReset?: () => void
}

export type UseDbFormWithValue<T extends PlainObject> = UseDbForm<T> & {value: T}

/** Helper function used to return a handler for the onChange event for the input fields. */
const getInputOnChange =
  <Value,>(setValue: (value: Value | ((current: Value) => Value)) => void) =>
  (val: Value | React.ChangeEvent<unknown> | ((current: Value) => Value)) => {
    if (!val) {
      setValue(val as Value)
    } else if (typeof val === 'function') {
      setValue(val)
    } else if (typeof val === 'object' && 'nativeEvent' in val) {
      const {currentTarget} = val
      if (currentTarget instanceof HTMLInputElement) {
        if (currentTarget.type === 'checkbox') {
          setValue(currentTarget.checked as any)
        } else {
          setValue(currentTarget.value as any)
        }
      } else if (currentTarget instanceof HTMLTextAreaElement || currentTarget instanceof HTMLSelectElement) {
        setValue(currentTarget.value as any)
      }
    } else {
      setValue(val)
    }
  }

/** allows you to work with a subset of the form specified with the path. You must provide T because I can't work out w way to infer that performs ok */
export const dbFormSubset = <T extends PlainObject>(
  form: {
    setValues: (values: any, description?: string) => any
    values?: any
    getInputProps: (path: any, options?: any) => any
    isDirty?: boolean
  },
  path: string,
  options?: {
    onChange?: ({delta, cfg, description}: {delta: DeltaValuesDynamic; cfg: T; description?: string}) => void
    defaultDescription?: string
  }
) => {
  const fullPath = (p: string | string[]) => [
    ...(path == '.' ? [] : path.split('.')),
    ...match(p)
      .with(P.array(P.string), _p => _p)
      .with(P.string, _p => (_p == '.' ? [] : _p.split('.')))
      .exhaustive()
  ]

  const values = path == '.' ? form.values : pathValue(form.values, path)
  const withDelta = (delta: DeltaValuesDynamic) => withDeltaValues(values, delta) as T

  const deltaWithFullPath = (delta: DeltaValuesDynamic) => mapKeys(delta, k => fullPath(k).join('.'))

  const setValues = (delta: DeltaValuesDynamic, description?: string) => {
    const fullPathDelta = mapKeys(delta, k => fullPath(k).join('.'))
    form.setValues(fullPathDelta, description ?? options?.defaultDescription)
    const cfg = withDelta(delta)
    if (options?.onChange) options.onChange({delta: delta, cfg, description})
    return cfg
  }

  const setFieldValue = (subPath: string | string[], value: Json | undefined, description?: string) =>
    setValues({[isArray(subPath) ? subPath.join('.') : subPath]: value}, description)

  const setFieldValueDynamic = (subPath: string | string[], value: Json | undefined, description?: string) =>
    setValues({[isArray(subPath) ? subPath.join('.') : subPath]: value}, description)

  const getInputProps = (subPath: string | string[], o?: any) => {
    const originalProps = form.getInputProps(fullPath(subPath).join('.'), o)
    const onChange = getInputOnChange<Json>(v => {
      if (isFunction(v)) {
        console.error('input change function not supported', v)
        return
      }
      setFieldValue(subPath, v, o?.description ?? options?.defaultDescription)
    })
    return {...originalProps, onChange}
  }

  return {
    setValues,
    setFieldValue,
    setFieldValueDynamic,
    values,
    getInputProps,
    isDirty: form.isDirty, // currently this reflects the whole state and not just this little part,
    deltaWithFullPath
  } as DbFormSubset<T>
}

export type DbFormSubset<T extends PlainObject> = Pick<
  UseDbForm<T>,
  'setValues' | 'getInputProps' | 'values' | 'setFieldValueDynamic' | 'setFieldValue' | 'isDirty'
> & {
  deltaWithFullPath: (delta: DeltaValuesDynamic) => DeltaValuesDynamic
}

/** shapes a mantine form to be a dbFormSubset */
export const dbFormSubsetMantine = <T extends PlainObject>(mantineForm: UseFormReturnType<T>) =>
  pipe(mantineForm, f => ({
    ...pick(f, ['values', 'setValues', 'getInputProps', 'validate', 'reset', 'errors']),
    isDirty: f.isDirty(),
    setFieldValue: f.setFieldValue as UseDbForm<T>['setFieldValue']
  }))
